// Per-menu rounding engine for the admin live preview. Logically identical to
// rushmyfood-backend/src/utils/menuMarkup.js — integer cents, explicit half-up
// tie-break, no Math.round/toFixed for the rounding decision. Used only to show
// the owner what a sample price becomes; the backend is authoritative.

export const ROUNDING_MODES = ['AS_IS', 'NEAREST', 'UP_INCREMENT', 'DOWN_INCREMENT', 'UP_ENDING', 'DOWN_ENDING']

export function roundPrice(amount, mode, value) {
  const amt = Number(amount)
  if (!Number.isFinite(amt) || amt <= 0) return 0

  const cents = Math.round(amt * 100)
  if (mode === 'AS_IS' || !ROUNDING_MODES.includes(mode)) return cents / 100

  const step = Math.round(Number(value) * 100)

  switch (mode) {
    case 'NEAREST': {
      if (!(step > 0)) return cents / 100
      const r = cents % step
      const rounded = (r * 2 >= step) ? (cents - r + step) : (cents - r)
      return rounded / 100
    }
    case 'UP_INCREMENT': {
      if (!(step > 0)) return cents / 100
      return (Math.ceil(cents / step) * step) / 100
    }
    case 'DOWN_INCREMENT': {
      if (!(step > 0)) return cents / 100
      return (Math.floor(cents / step) * step) / 100
    }
    case 'UP_ENDING': {
      const ending = ((step % 100) + 100) % 100
      const base = Math.floor(cents / 100) * 100 + ending
      const out = base >= cents ? base : base + 100
      return out / 100
    }
    case 'DOWN_ENDING': {
      const ending = ((step % 100) + 100) % 100
      const base = Math.floor(cents / 100) * 100 + ending
      const out = base <= cents ? base : base - 100
      return out / 100
    }
    default:
      return cents / 100
  }
}

// Mark up + round a single price by the menu's rule (identity if none).
export function roundComponentForMenu(price, markupPercent, roundingMode = 'AS_IS', roundingValue = 0) {
  const base = Number(price)
  if (!Number.isFinite(base)) return 0
  const pct = Number(markupPercent)
  const marked = Number.isFinite(pct) && pct > 0 ? base * (1 + pct / 100) : base
  if (!(marked > 0)) return 0
  return roundPrice(marked, roundingMode, roundingValue)
}

// ── Per-category / per-item overrides (mirror backend menuMarkup.js) ─────────
function asObject(v) {
  if (!v) return null
  if (typeof v === 'string') {
    try { v = JSON.parse(v) } catch (e) { return null }
  }
  return v && typeof v === 'object' ? v : null
}

function isUsableRule(r) {
  if (!r || typeof r !== 'object') return false
  const pct = Number(r.pct)
  const hasMarkup = Number.isFinite(pct) && pct > 0
  const hasRounding = r.mode && r.mode !== 'AS_IS' && Number.isFinite(Number(r.value))
  return hasMarkup || hasRounding
}

// Resolve the EFFECTIVE pricing for ONE item given its menu's overrides.
// Precedence: per-item override > per-category override > menu default.
// Used by the admin item-list/preview so it shows the real final price.
export function resolveItemPricing(item, menu) {
  const menuRule = {
    pct: Number(menu && menu.markupPercent) || 0,
    mode: (menu && menu.roundingMode) || 'AS_IS',
    value: Number(menu && menu.roundingValue) || 0,
  }
  const itemId = item && (item.id != null ? item.id : item._id)
  const itemOverrides = asObject(menu && menu.itemOverrides) || {}
  const categoryOverrides = asObject(menu && menu.categoryOverrides) || {}

  const io = itemId != null ? itemOverrides[String(itemId)] : null
  if (io && typeof io === 'object') {
    if (io.type === 'FIXED') {
      const basePrice = io.basePrice != null && Number.isFinite(Number(io.basePrice)) ? Number(io.basePrice) : null
      const vp = asObject(io.variantPrices)
      const variantPrices = vp
        ? Object.fromEntries(Object.entries(vp)
            .filter(([, p]) => Number.isFinite(Number(p)))
            .map(([k, p]) => [k, Number(p)]))
        : null
      if (basePrice != null || (variantPrices && Object.keys(variantPrices).length)) {
        return { kind: 'FIXED', basePrice, variantPrices }
      }
    } else {
      const rule = { pct: Number(io.pct) || 0, mode: io.mode || 'AS_IS', value: Number(io.value) || 0 }
      if (isUsableRule(rule)) return { kind: 'MARKUP', ...rule }
    }
  }

  const catName = item && item.categoryName
  const co = catName != null ? categoryOverrides[String(catName)] : null
  if (co && typeof co === 'object') {
    const rule = { pct: Number(co.pct) || 0, mode: co.mode || 'AS_IS', value: Number(co.value) || 0 }
    if (isUsableRule(rule)) return { kind: 'MARKUP', ...rule }
  }

  return { kind: 'MARKUP', ...menuRule }
}

// Human-readable rule options for the admin select. `needsValue` drives whether
// the value input is shown; `valueLabel` tells the owner what to type.
export const ROUNDING_OPTIONS = [
  { value: 'AS_IS', label: 'As-is (markup only, no rounding)', needsValue: false },
  { value: 'NEAREST', label: 'Nearest increment', needsValue: true, valueLabel: 'Increment (e.g. 0.25)', hint: 'e.g. 0.25 snaps to the nearest 0.25 ($10.95 → $11.00)' },
  { value: 'UP_INCREMENT', label: 'Round up to increment', needsValue: true, valueLabel: 'Increment (e.g. 0.25)', hint: 'e.g. 0.25 rounds up to the next 0.25 ($10.95 → $11.00). NOTE: not a price-ending.' },
  { value: 'DOWN_INCREMENT', label: 'Round down to increment', needsValue: true, valueLabel: 'Increment (e.g. 0.25)', hint: 'e.g. 0.25 rounds down to the previous 0.25 ($10.95 → $10.75).' },
  { value: 'UP_ENDING', label: 'Round up to price ending', needsValue: true, valueLabel: 'Ending (e.g. 0.99)', hint: 'e.g. 0.99 makes prices end in .99 ($10.95 → $10.99). This is the .99 pricing most people want.' },
  { value: 'DOWN_ENDING', label: 'Round down to price ending', needsValue: true, valueLabel: 'Ending (e.g. 0.00)', hint: 'e.g. 0.00 makes prices whole dollars, rounding down ($10.95 → $10.00).' },
]

// One-line plain-English description of the active rule, using the owner's ACTUAL
// typed value so the sentence always matches the preview numbers.
export function describeRule(mode, value) {
  const v = Number(value)
  const isEnding = mode === 'UP_ENDING' || mode === 'DOWN_ENDING'
  if (mode === 'AS_IS') return 'No rounding — the exact marked-up price is shown.'
  const empty = value === '' || value === null || value === undefined || isNaN(v)
  if (empty || (!isEnding && !(v > 0))) return 'Enter a value to apply this rule.'
  const amt = `$${v.toFixed(2)}`
  const ending = `.${String(Math.round(v * 100)).padStart(2, '0')}`
  // Compute a real worked example from the engine so the sentence is never wrong.
  const ex = (sample) => `$${sample.toFixed(2)} → $${roundPrice(sample, mode, v).toFixed(2)}`
  switch (mode) {
    case 'NEAREST':        return `Snaps each price to the nearest ${amt}, up or down (${ex(10.95)}).`
    case 'UP_INCREMENT':   return `Rounds each price UP to the next multiple of ${amt} — a step size, not a price-ending (${ex(10.95)}).`
    case 'DOWN_INCREMENT': return `Rounds each price DOWN to the previous multiple of ${amt} — a step size, not a price-ending (${ex(10.95)}).`
    case 'UP_ENDING':      return `Makes every price end in ${ending}, rounding UP (${ex(10.45)}).`
    case 'DOWN_ENDING':    return `Makes every price end in ${ending}, rounding DOWN (${ex(10.95)}).`
    default:               return ''
  }
}